import { HTMLProps, useEffect, useMemo, useState } from "react";
import { cn, formatValue } from "@/utils/helpers";
import { useAppStore } from "@/store/app";
import { getLang, getUnit } from "@/utils/selectors";
import styles from "../Map.module.css"
import Overlay from "@/components/common/Overlay";
import Svg from "@/components/common/Svg";
import { useDataStore } from "@/store/data";

export interface MapLegendProps extends HTMLProps<HTMLElement> {}

export default function MapLegend({
	className
} : MapLegendProps) {
	const [colors, setColors] = useState<string[]>([]);
	const controls = useAppStore(store => store.controls);
	const extents = useDataStore(store => store.extents);
	const getExtents = useDataStore(store => store.getExtents);

	const unit = useMemo(() =>
		getUnit(controls.stat)
	, [controls]);

	const currentExtents = useMemo(() =>
		getExtents(controls.geo, controls.view, controls.stat)?.reverse()
	, [extents, controls, getExtents]);

	useEffect(() => {
		const bodyStyles = typeof window !== "undefined"
			? window?.getComputedStyle(document.body)
			: null;
		const newColors = currentExtents.map((value: number | undefined, index: number) =>
			bodyStyles?.getPropertyValue(`--color-${controls.view}-choropleth-${index + 1}`) || "transparent",
		).reverse();
		setColors(newColors);
	}, [controls.view, currentExtents]);

	return (
		<Overlay
			className={cn(
				styles.MapLegend,
				className
			)}
		>
			<div
				className={styles.MapLegendUnit}
			>
				{getLang("unit", unit)}
			</div>
			<div
				className={styles.MapLegendScale}
			>
				<div
					className={styles.MapLegendScaleAxis}
				>
					{currentExtents.map((value: number | undefined, index: number) =>
						<div
							key={index}
							className={styles.MapLegendScaleAxisTick}
						>
							{formatValue(
								value,
								controls.stat
							)}
						</div>
					)}
				</div>
				<Svg
					className={styles.MapLegendScaleBar}
				>
					<defs>
						<linearGradient
							id="MapLegendScaleGradient"
							x1="0%"
							y1="0%"
							x2="0%"
							y2="100%"
						>
							{colors.map((color: string, index: number) =>
								<stop
									key={index}
									offset={`${(index / colors.length) * 100}%`}
									stopColor={color}
								/>
							)}
						</linearGradient>
					</defs>
					<rect
						x="0"
						y="0"
						rx="50%"
						width="100%"
						height="100%"
						fill="url(#MapLegendScaleGradient)"
					/>
				</Svg>
			</div>
		</Overlay>
	)
}